import { useToast } from "@mallardbay/lib-react-components"

import { TypeName } from "~config/constants"
import { useUser } from "~config/user-provider"
import type {
    ActivityFieldsFragment,
    FeedGroup,
    Reaction,
    ReactionType,
} from "~graphql/generated/graphql"
import {
    useFeedActivitiesQuery,
    useReactToActivityMutation,
    useRemoveReactionMutation,
    useStreamActivitiesByIdsQuery,
} from "~graphql/generated/graphql"

function useStreamActivitiesByIds(ids: string[]) {
    const { data, loading, error } = useStreamActivitiesByIdsQuery({
        variables: { ids },
        skip: !ids.length,
    })

    return {
        activities: data?.streamActivitiesByIds ?? [],
        isLoading: loading,
        error,
    }
}

export function useStreamActivityById(id?: string | null) {
    const { activities, isLoading, error } = useStreamActivitiesByIds(
        id ? [id] : []
    )

    return {
        activity: activities[0],
        isLoading,
        error,
    }
}

enum ReactionAction {
    ADD = "ADD",
    REMOVE = "REMOVE",
}

function getUpdatedOwnReactions({
    activity,
    reactionType,
    action,
    userId,
}: {
    activity: ActivityFieldsFragment
    reactionType: ReactionType
    action: ReactionAction
    userId: string
}) {
    if (action === ReactionAction.ADD) {
        const ownReactions = { ...activity.own_reactions }
        const updatedReactions = ownReactions[reactionType] ?? []

        updatedReactions.push({
            __typename: TypeName.Reaction,
            activity_id: activity.id,
            id: "temp-id",
            user_id: userId,
        })
        return {
            ...ownReactions,
            [reactionType]: updatedReactions,
        }
    }

    // Remove reaction
    return {
        ...activity.own_reactions,
        [reactionType]: null,
    }
}

function getUpdatedReactionCounts({
    activity,
    reactionType,
    action,
}: {
    activity: ActivityFieldsFragment
    reactionType: ReactionType
    action: ReactionAction
}) {
    if (action === ReactionAction.ADD) {
        return {
            ...activity.reaction_counts,
            [reactionType]: (activity.reaction_counts?.[reactionType] ?? 0) + 1,
        }
    }

    if (!activity.reaction_counts?.[reactionType]) {
        return activity.reaction_counts
    }

    // Remove reaction
    return {
        ...activity.reaction_counts,
        [reactionType]: (activity.reaction_counts[reactionType] || 0) - 1,
    }
}

function getOptimisticActivity({
    activity,
    userId,
    reactionType,
    action,
}: {
    activity: ActivityFieldsFragment
    userId: string
    reactionType: ReactionType
    action: ReactionAction
}) {
    return {
        ...activity,
        own_reactions: getUpdatedOwnReactions({
            activity,
            reactionType,
            action,
            userId,
        }),
        reaction_counts: getUpdatedReactionCounts({
            activity,
            reactionType,
            action,
        }),
    }
}

function getOptimisticReactionResponse({
    activity,
    reactionType,
    userId,
}: {
    activity: ActivityFieldsFragment
    reactionType: ReactionType
    userId: string
}) {
    return {
        reactToActivity: getOptimisticActivity({
            activity,
            userId,
            reactionType,
            action: ReactionAction.ADD,
        }),
    }
}

function getOptimisticReactionRemovalResponse({
    activity,
    reactionType,
    userId,
}: {
    activity: ActivityFieldsFragment
    reactionType: ReactionType
    userId: string
}) {
    return {
        removeReaction: getOptimisticActivity({
            activity,
            userId,
            reactionType,
            action: ReactionAction.REMOVE,
        }),
    }
}

export function useRemoveReaction({
    activity,
    reaction,
    reactionType,
}: {
    activity: ActivityFieldsFragment
    reaction?: Reaction
    reactionType: ReactionType
}) {
    const { user } = useUser()
    const { showError } = useToast()
    const [removeReactionMutation, { loading }] = useRemoveReactionMutation({
        variables: { reactionId: reaction?.id || "" },
        onError: showError,
    })

    const removeReaction = () => {
        const optimisticResponse = getOptimisticReactionRemovalResponse({
            activity,
            reactionType,
            userId: user?.id ?? "",
        })

        void removeReactionMutation({ optimisticResponse })
    }

    return { removeReaction, isLoading: loading }
}

export function useReactToActivity({
    activity,
    reactionType,
}: {
    activity: ActivityFieldsFragment
    reactionType: ReactionType
}) {
    const { user } = useUser()
    const { showError } = useToast()
    const [reactToActivityMutation, { loading }] = useReactToActivityMutation({
        variables: { activityId: activity.id, reactionType },
        onError: showError,
    })

    const reactToActivity = () => {
        const optimisticResponse = getOptimisticReactionResponse({
            activity,
            reactionType,
            userId: user?.id ?? "",
        })

        void reactToActivityMutation({ optimisticResponse })
    }

    return { reactToActivity, isLoading: loading }
}

export function useStreamFeed({
    feedId,
    feedGroup,
    limit = 10,
    offset = 0,
}: {
    feedId: string
    feedGroup: FeedGroup
    limit?: number
    offset?: number
}) {
    const {
        data,
        loading,
        error,
        fetchMore: fetchMoreCore,
    } = useFeedActivitiesQuery({
        variables: {
            feedId,
            feedGroup,
            limit,
            offset,
        },
    })

    const feedActivitiesData = data?.feedActivities

    const canLoadMore = feedActivitiesData?.canFetchMore
    const activities = feedActivitiesData?.results || []

    const fetchMore = () => {
        return fetchMoreCore({
            variables: {
                offset: activities.length,
            },
        })
    }

    return {
        activities: feedActivitiesData?.results || [],
        isLoading: loading,
        error,
        fetchMore,
        hasMore: canLoadMore,
    }
}
